import React from 'react';
import { Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderTransportDto } from '../../../dto/transport';
import { formatCurrency } from '../../../utils/currency';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { getTransportLabel } from '../../../utils/transportLabel';
import type { TransportKind } from '../../transports/transportDirection';

interface SupplierOrderTransportStripProps {
  /** Trucks of this ZD; null = not loaded (draft stage, or the list failed). */
  transports: SupplierOrderTransportDto[] | null;
  onOpenTransport: (transportId: string) => void;
}

const STATUS_KEYS: Record<string, string> = {
  PENDING: 'pending',
  SCHEDULED: 'scheduled',
  LOADING: 'loading',
  IN_TRANSIT: 'in_transit',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  PLANNED: 'planned',
  DELAYED: 'delayed',
};

/** Owner's order of the groups: to the warehouse, direct, from the warehouse. */
const GROUP_ORDER: TransportKind[] = ['FACTORY_TO_WAREHOUSE', 'FACTORY_TO_CUSTOMER', 'WAREHOUSE_TO_CUSTOMER'];

/**
 * „Transporty dostawy” strip of the ZD header (makieta `renderHeader`): one
 * row per truck — label, status, loading date, freight (cost roles only) and
 * the link to the truck — grouped by rodzaj auta (backend `kind`); a group
 * heading shows only when it has trucks, trucks of unknown kind (older
 * response) follow without a heading. Before any truck exists it says when
 * to plan one.
 */
export default function SupplierOrderTransportStrip({ transports, onOpenTransport }: SupplierOrderTransportStripProps) {
  const { t } = useTranslation(['orders', 'transport']);
  const rows = transports ?? [];
  const key = (name: string) => t(`orders:supplierOrders.card.header.${name}`);
  const groups = GROUP_ORDER
    .map((kind) => ({ kind, trucks: rows.filter((transport) => transport.kind === kind) }))
    .filter((group) => group.trucks.length > 0);
  const ungrouped = rows.filter((transport) => !transport.kind || !GROUP_ORDER.includes(transport.kind));

  const renderRow = (transport: SupplierOrderTransportDto) => (
    <div className="zd-strip__row" key={transport.id} data-testid="supplier-order-transport-row">
      <b>{getTransportLabel(transport)}</b>
      <Tag color={transport.status === 'DELIVERED' ? 'green' : transport.status === 'IN_TRANSIT' ? 'blue' : undefined}>
        {t(`transport:status.${STATUS_KEYS[transport.status] ?? 'pending'}`)}
      </Tag>
      <span className="zd-strip__muted">
        {transport.transportCompany}
        {transport.loadingDate ? ` · ${t('orders:supplierOrders.card.header.truckLoading', { date: formatDateEuropean(transport.loadingDate) })}` : ''}
        {transport.freight?.transportRate != null
          ? ` · ${t('orders:supplierOrders.card.header.truckFreight', { amount: formatCurrency(transport.freight.transportRate, transport.freight.currency) })}`
          : ''}
      </span>
      <Button type="link" size="small" onClick={() => onOpenTransport(transport.id)}>{key('trucksOpen')}</Button>
    </div>
  );

  return (
    <div className="zd-strip" data-testid="supplier-order-transport-strip">
      <div className="zd-strip__head">
        <span>{key('trucksTitle')}</span>
        <Tag>{rows.length ? rows.length : key('trucksNone')}</Tag>
      </div>
      {rows.length === 0 ? <div className="zd-strip__empty">{key('trucksEmpty')}</div> : (
        <>
          {groups.map((group) => (
            <React.Fragment key={group.kind}>
              <div className="zd-strip__group" data-testid={`supplier-order-transport-group-${group.kind}`}>
                {key(`trucksGroup.${group.kind}`)}
              </div>
              {group.trucks.map(renderRow)}
            </React.Fragment>
          ))}
          {ungrouped.map(renderRow)}
        </>
      )}
    </div>
  );
}
